import React, { useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import PermissionGate from '../common/PermissionGate';
import {
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Add as AddIcon,
  Description as MarkdownIcon,
  PictureAsPdf as PdfIcon,
  Article as DocxIcon,
  Code as HtmlIcon,
  TextSnippet as TxtIcon
} from '@mui/icons-material';
import FullScreenButton from '../common/FullScreenButton';
import api from '../../services/api';

/**
 * Helper: Trigger file download from blob
 */
const triggerDownload = (content, filename, mimeType) => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Export content via the backend unified export API for formats that need
 * server-side processing (docx, pdf, html, txt), or client-side for md.
 *
 * @param {string} format - Export format (md, pdf, docx, html, txt)
 * @param {string} markdownContent - The markdown content to export
 * @param {string} baseFilename - Base filename without extension
 * @param {string} [conversationId] - Optional conversation ID for backend export
 * @param {string} [projectId] - Optional project ID for backend export
 */
const exportAsFormat = async (format, markdownContent, baseFilename, conversationId, projectId) => {
  /**
   * Helper to download blob from backend response
   */
  const downloadFromResponse = (response, defaultFilename) => {
    const contentDisposition = response.headers['content-disposition'];
    let filename = defaultFilename;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }
    triggerDownload(response.data, filename, response.headers['content-type']);
  };

  // Strategy 1: If we have a conversationId, use conversation export endpoint
  if (conversationId) {
    try {
      const response = await api.post(
        `/export/${format}/conversations/${conversationId}`,
        {
          title: baseFilename,
          include_toc: true,
          include_cover: true,
          include_diagrams: true,
        },
        { responseType: 'blob' }
      );
      downloadFromResponse(response, `${baseFilename}.${format}`);
      return;
    } catch (error) {
      console.warn(`Backend conversation export failed for ${format}:`, error);
    }
  }

  // Strategy 2: Send raw content to backend content export endpoint
  try {
    const response = await api.post(
      `/export/${format}/content`,
      {
        content: markdownContent,
        title: baseFilename,
        include_toc: true,
        include_cover: true,
        include_diagrams: true,
        project_id: projectId || undefined,
      },
      { responseType: 'blob' }
    );
    downloadFromResponse(response, `${baseFilename}.${format}`);
    return;
  } catch (error) {
    console.warn(`Backend content export failed for ${format}:`, error);
  }

  // Strategy 3: Client-side fallback (only if backend is completely unavailable)
  console.warn(`All backend exports failed for ${format}, using client-side fallback`);
  if (format === 'pdf') {
    try {
      const { downloadAsPdf } = await import('../../utils/simplePdfGenerator');
      downloadAsPdf(markdownContent, baseFilename);
      return;
    } catch (pdfErr) {
      console.warn('Client-side PDF generation failed:', pdfErr);
    }
  }
  // Final fallback: download as the closest available format
  const fallbackExt = format === 'html' ? 'html' : format === 'txt' ? 'txt' : 'md';
  const fallbackMime = format === 'html' ? 'text/html' : format === 'txt' ? 'text/plain' : 'text/markdown';
  triggerDownload(markdownContent, `${baseFilename}.${fallbackExt}`, fallbackMime);
};

/**
 * TopBarButtons - A reusable atom component for feature panel top bar actions
 *
 * @param {Object} props
 * @param {boolean} props.isFullScreen - Whether the view is in full screen mode
 * @param {Function} props.onToggleFullScreen - Handler for full screen toggle
 * @param {Function} props.onAdd - Handler for add action (optional)
 * @param {Function} props.onExport - Handler for export action (single format, legacy)
 * @param {Function} props.getExportContent - Function that returns { content: string, filename: string } for multi-format export
 * @param {boolean} props.isExporting - Whether export is in progress
 * @param {Function} props.onCopy - Handler for copy action
 * @param {Function} props.onRefresh - Handler for refresh action
 * @param {boolean} props.isRefreshing - Whether refresh is in progress (spins the icon and disables the button)
 * @param {Function} props.onSave - Handler for save action (optional)
 * @param {boolean} props.isSaving - Whether save is in progress (optional)
 * @param {Function} props.onZoomIn - Handler for zoom in action (optional)
 * @param {Function} props.onZoomOut - Handler for zoom out action (optional)
 * @param {number} props.zoomLevel - Current zoom level (optional)
 * @param {number} props.maxZoom - Maximum zoom level (default: 2.0)
 * @param {number} props.minZoom - Minimum zoom level (default: 0.5)
 * @param {boolean} props.disabled - Whether actions should be disabled
 * @param {string} props.addTooltip - Custom tooltip for add button
 * @param {string} props.exportTooltip - Custom tooltip for export button
 * @param {string} props.copyTooltip - Custom tooltip for copy button
 * @param {string} props.refreshTooltip - Custom tooltip for refresh button
 * @param {string} props.saveTooltip - Custom tooltip for save button
 * @param {Object} props.sx - Additional styling
 */
const TopBarButtons = ({
  isFullScreen,
  onToggleFullScreen,
  onAdd,
  onExport,
  getExportContent,
  isExporting = false,
  onCopy,
  onRefresh,
  isRefreshing = false,
  onSave,
  isSaving = false,
  onZoomIn,
  onZoomOut,
  zoomLevel = 1,
  maxZoom = 2.0,
  minZoom = 0.5,
  disabled = false,
  addTooltip = "Add new",
  exportTooltip = "Export",
  copyTooltip = "Copy to clipboard",
  refreshTooltip = "Refresh",
  saveTooltip = "Save as document",
  sx = {}
}) => {
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [isExportingFormat, setIsExportingFormat] = useState(false);
  const buttonSx = { color: '#485869' };

  const handleFormatExport = async (format) => {
    setDownloadMenuAnchor(null);
    if (!getExportContent) return;

    setIsExportingFormat(true);
    try {
      const exportData = getExportContent();
      const { content, filename, conversationId: convId, projectId: projId } = exportData || {};
      if (content) {
        await exportAsFormat(format, content, filename, convId, projId);
      }
    } catch (error) {
      console.error(`Error exporting as ${format}:`, error);
    } finally {
      setIsExportingFormat(false);
    }
  };

  const showMultiFormat = !!getExportContent;
  const exporting = isExporting || isExportingFormat;

  return (
    <Box sx={{ display: 'flex', gap: 1, ...sx }}>
      {/* Add button */}
      {onAdd && (
        <PermissionGate permission="conversation.content.add">
          <Tooltip title={addTooltip}>
            <IconButton
              size="small"
              onClick={onAdd}
              disabled={disabled}
              sx={buttonSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PermissionGate>
      )}

      {/* Full screen toggle */}
      <FullScreenButton
        isFullScreen={isFullScreen}
        onToggle={onToggleFullScreen}
      />

      {/* Export button */}
      <PermissionGate permission="conversation.content.download">
        {showMultiFormat ? (
          <>
            <Tooltip title={exportTooltip}>
              <IconButton
                size="small"
                onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                disabled={exporting || disabled}
                sx={buttonSx}
              >
                {exporting ?
                  <CircularProgress size={18} /> :
                  <DownloadIcon fontSize="small" />
                }
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleFormatExport('md')}>
                <ListItemIcon><MarkdownIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Markdown (.md)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleFormatExport('pdf')}>
                <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
                <ListItemText>PDF (.pdf)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleFormatExport('docx')}>
                <ListItemIcon><DocxIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Word (.docx)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleFormatExport('html')}>
                <ListItemIcon><HtmlIcon fontSize="small" /></ListItemIcon>
                <ListItemText>HTML (.html)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleFormatExport('txt')}>
                <ListItemIcon><TxtIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Plain Text (.txt)</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Tooltip title={exportTooltip}>
            <IconButton
              size="small"
              onClick={onExport}
              disabled={exporting || disabled}
              sx={buttonSx}
            >
              {exporting ?
                <CircularProgress size={18} /> :
                <DownloadIcon fontSize="small" />
              }
            </IconButton>
          </Tooltip>
        )}
      </PermissionGate>

      {/* Copy button */}
      <Tooltip title={copyTooltip}>
        <IconButton
          size="small"
          onClick={onCopy}
          disabled={disabled}
          sx={buttonSx}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Zoom out button */}
      {onZoomOut && (
        <Tooltip title="Zoom out">
          <IconButton
            size="small"
            onClick={onZoomOut}
            disabled={zoomLevel <= minZoom}
            sx={buttonSx}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Zoom in button */}
      {onZoomIn && (
        <Tooltip title="Zoom in">
          <IconButton
            size="small"
            onClick={onZoomIn}
            disabled={zoomLevel >= maxZoom}
            sx={buttonSx}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Refresh button */}
      <Tooltip title={isRefreshing ? 'Refreshing…' : refreshTooltip}>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{
              ...buttonSx,
              // Pronounced "working" state: dim + slight backdrop so the
              // user clearly sees the icon change immediately on click.
              // pointerEvents:'auto' is required so the Tooltip still
              // detects hover while the button is disabled.
              '&.Mui-disabled': {
                color: '#485869',
                opacity: 0.5,
                backgroundColor: 'rgba(72, 88, 105, 0.08)',
                pointerEvents: 'auto',
                cursor: 'wait',
              },
            }}
          >
            <RefreshIcon
              fontSize="small"
              sx={{
                animation: isRefreshing ? 'tb-refresh-spin 0.8s linear infinite' : 'none',
                '@keyframes tb-refresh-spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default TopBarButtons;
